import { useCallback } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { WorkflowEditorMode } from '@/enums/workflow-editor-mode.enum';
import { I18N_KEYS } from '@/language/keys';
import { useAppOutputMutations, useWorkflowAppByWorkflowIdQuery } from '@/queries/useWorkflowApp';
import { useWorkflowStore } from '@/state/workflow.state';
import { Typography } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { MinusIcon } from '@/UI/Icons';
import { useAppBuilderContext } from '@/components/Recipe/Drawer/useAppBuilderState';
import { useWorkflowEditorMode } from '@/components/Recipe/FlowComponents/WorkflowRightToolbar/workflowEditorMode.store';
import type { MouseEvent, ReactNode } from 'react';
import type { NodeId } from 'web';

export function AppBuilderOutputOverlay({ children, id }: { children: ReactNode; id: NodeId }) {
  const { t } = useTranslation();
  const recipeId = useWorkflowStore((state) => state.recipe.id);
  const { workflowEditorMode } = useWorkflowEditorMode();
  const isAppBuilderMode = workflowEditorMode === WorkflowEditorMode.AppBuilder;
  const { data: workflowApp } = useWorkflowAppByWorkflowIdQuery(isAppBuilderMode ? recipeId : undefined);
  const { setAsOutput, removeOutput, isUpdatingOutput } = useAppOutputMutations(workflowApp);
  const { isEligibleOutputNode, isWorkflowAppOutputNode } = useAppBuilderContext(id);

  const handleSetAsOutput = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAsOutput(id);
    },
    [id, setAsOutput],
  );

  const handleRemoveOutput = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      removeOutput(id);
    },
    [id, removeOutput],
  );

  if (!isAppBuilderMode) {
    return <>{children}</>;
  }

  const shouldShowOutputBorder = isEligibleOutputNode || isWorkflowAppOutputNode;
  const nodeContent = shouldShowOutputBorder ? (
    <Box
      sx={{
        border: `1px solid ${color.Yellow40}`,
        borderRadius: 4,
      }}
    >
      {children}
    </Box>
  ) : (
    children
  );

  const content = isWorkflowAppOutputNode ? (
    <Box
      sx={{
        borderRadius: 4,
        backgroundColor: color.Yellow64,
        p: 0.25,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: 1,
          pr: 0.5,
          py: 0.5,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography variant="body-lg-rg" color={color.Black100}>
            {t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.OUTPUT_BADGE)}
          </Typography>
        </Box>
        <AppIconButton
          aria-label={t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.REMOVE_OUTPUT_ARIA)}
          disabled={!workflowApp || isUpdatingOutput}
          onClick={handleRemoveOutput}
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            color: color.Black100,
            '&:hover': {
              backgroundColor: color.Yellow64,
              color: color.Black100,
            },
            '&:disabled': {
              color: color.Black40,
            },
          }}
        >
          <MinusIcon width={16} height={16} strokeWidth={1.5} />
        </AppIconButton>
      </Box>
      {nodeContent}
    </Box>
  ) : (
    nodeContent
  );

  if (!isEligibleOutputNode || isWorkflowAppOutputNode) {
    return content;
  }

  return (
    <Box>
      {content}
      <Box
        sx={{
          position: 'absolute',
          zIndex: 3,
          top: -42,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <ButtonContained
          size="medium"
          type="button"
          disabled={!workflowApp || isUpdatingOutput}
          onClick={handleSetAsOutput}
          sx={{
            height: 34,
            bgcolor: color.Yellow40,
            fontSize: '16px',
            '&:hover': {
              bgcolor: color.Yellow100,
            },
          }}
        >
          {t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.SET_AS_OUTPUT)}
        </ButtonContained>
      </Box>
    </Box>
  );
}
